import React from 'react';
import { FormControlLabel, Checkbox, Select, MenuItem } from '@mui/material';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import { MoneyType } from '../../../PriceMatrix/types';
import SegmentCollapse, {
	Props as SegmentCollapseProps
} from '../../../AdditionalServiceWrapper/components/SegmentCollapse/SegmentCollapse';
import Money from '../../../Money';

interface Option {
	id: string;
	label: string;
	value: string;
	disabled?: boolean;
	price?: MoneyType;
}

interface Props extends Omit<SegmentCollapseProps, 'type' | 'onChange'> {
	optionClassName?: string;
	containerClassName?: string;
	type?: 'checkbox' | 'select';
	value?: string;
	options: Option[];
	onChange: (value?: string, checked?: boolean) => void;
	onChangeCollapse?: (checked: boolean) => void;
	renderOptionPayload?: (option: Option) => React.ReactNode;
}

const Control: React.FC<Props> = ({
	optionClassName,
	containerClassName,
	type = 'select',
	value,
	options,
	onChange,
	onChangeCollapse,
	...props
}) => {
	const { Control: theme } = useTheme('CheckoutAdditionalService');

	const onChangeCheckbox = (id: string) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		onChange(id, checked);
	};

	const onChangeSelect = (
		e:
			| React.ChangeEvent<Omit<HTMLInputElement, 'value'> & { value: string }>
			| (Event & { target: { value: string; name: string } })
	) => {
		onChange(e.target.value as string);
	};

	const content =
		type === 'checkbox' ? (
			<>
				{options.map((option, index) => (
					<div key={index} className={cn(theme.container, containerClassName)}>
						<div className={cn(theme.checkbox, optionClassName)}>
							<FormControlLabel
								value={'end'}
								label={option.label}
								classes={{
									label: theme.checkbox__label
								}}
								disabled={option.disabled || props.isDisabled}
								control={
									<Checkbox
										classes={{
											root: theme.checkbox__root,
											checked: theme.checkbox__checked
										}}
										disableRipple={true}
										checked={!!option.value}
										onChange={onChangeCheckbox(option.id)}
									/>
								}
							/>
							{option.price && (
								<div className={theme.checkbox__price}>
									<Money money={option.price} />
								</div>
							)}
						</div>

						{props.renderOptionPayload?.(option)}
					</div>
				))}
			</>
		) : (
			<Select
				className={cn(theme.select, optionClassName)}
				classes={{ root: theme.select_root }}
				onChange={onChangeSelect}
				disableUnderline={true}
				disabled={props.isDisabled}
				value={value ?? options[0].value}
			>
				{options.map((option, index) => (
					<MenuItem value={option.value} key={index}>
						<span>{option.label}</span>
						{option.price && (
							<>
								&nbsp;-
								<Money money={option.price} />
							</>
						)}
					</MenuItem>
				))}
			</Select>
		);

	return (
		<SegmentCollapse onChangeCheckbox={onChangeCollapse} {...props}>
			{content}
		</SegmentCollapse>
	);
};

export default Control;
